import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { fechaLarga } from '../../lib/format'
import { Check, ChevronDown } from 'lucide-react'
import { CordBraid, CordMark, FASES, puntosDeHebra, useReveal } from '../../ui/Cord'
import { Spinner } from '../../ui'
import { cx } from './secciones/comunes'

/* ===========================================================================
   Las dos preguntas.

   Se abre una sola vez en la vida, en el celular de Vanessa, con Lenin al
   lado grabando. Son dos preguntas encadenadas —«¿quieres ser mi novia?» y,
   cuando ella cree que ya terminó, «¿te quieres casar conmigo?»— y lo que
   las une es el cordón de tres dobleces, la firma de todo el sitio:

     · Dos hebras son ella y él, eligiéndose. Eso es el noviazgo.
     · Dos hebras solas se pueden romper.
     · La tercera es Dios, y un cordón de tres no se trenza para una
       temporada (Eclesiastés 4:12). Eso es el matrimonio.

   El cordón no decora: es el argumento. Ella entiende POR QUÉ viene la
   segunda pregunta justo antes de que se la hagan.

   Nueve actos, en este orden:
     acceso → apertura → recuerdos → pregunta-novia → si-novia
       → cordon → pregunta-boda → mirada → pacto

   Las dos respuestas quedan selladas con su hora en el navegador —y, de
   respaldo, en el servidor—. Al volver, la página abre en el pacto: una
   pregunta así no se hace dos veces.

   Nada de esta página depende de la red en el momento: el sello vive en el
   teléfono, las fotos van precargadas y el recuerdo en PNG se dibuja aquí
   mismo. Si esa noche no hay señal, no se nota.

   Dos salidas para los ensayos de Lenin, que va a abrir este enlace antes:
     · ?ensayo    — recorre todo sin escribir nada ni avisar a nadie. ES EL
                    QUE SE USA PARA ENSAYAR: es el único que no toca el
                    servidor.
     · ?reiniciar — borra los dos sellos DEL TELÉFONO y vuelve a empezar. El
                    del servidor no lo toca, y no puede: esta página es
                    pública. Si un ensayo se coló sin `?ensayo` y dejó puesta
                    una hora que no es, se limpia con sesión de dueño:
                    DELETE /api/weddings/:weddingId/propuesta/sello.
   =========================================================================== */

/** El id de la boda piloto, tal como está sembrado en `seeds/pilot.sql`. Con
 *  él se arman las URL de las fotos: `/f/<weddingId>/<photoId>`. */
const WEDDING_ID = 'wed_vaneylenindiciembr'

/** El mismo slug que `WEDDING_SLUG` en `wrangler.jsonc` (y que
 *  `SLUG_BODA_PRINCIPAL` en `main.tsx`). Aquí solo se usa para avisar del sí. */
const SLUG_BODA = 'vane-y-lenin'

/* El día en que dijo que sí a ser mi novia. NO fue por esta página: fue en
   persona, el 19 de agosto de 2026. Aquí solo se recuerda, y por eso este acto
   ya no pregunta nada — preguntárselo otra vez sería borrar ese día. */
const NOVIAZGO = '2026-08-19'

const CLAVE_NOVIA = 'nuestra-boda.propuesta.novia'
const CLAVE_BODA = 'nuestra-boda.propuesta.boda'

/** Tinta (`--color-tinta` de index.css). Se repite aquí porque hay que
 *  pintarla en el documento y en el lienzo, donde no llegan las clases. */
const TINTA = '#14212b'
const BRUMA = '#edefe8'
const PALO = '#c9cec0'
const LATON_CLARO = '#d3a866'

/** La familia de `--font-display`, copiada literal de src/client/index.css:
 *  el lienzo no lee variables CSS. */
const FAMILIA_DISPLAY =
  "'Iowan Old Style', 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, 'Times New Roman', serif"

// ---------------------------------------------------------------------------
// Los nueve momentos
// ---------------------------------------------------------------------------

export interface Momento {
  /** Id de la foto en `photos`, subida con `kind='propuesta'` (se sirve por
   *  `/f/` pero no sale en ninguna galería). Vacío en los dos primeros
   *  momentos, que no tienen foto: entonces se pinta solo el texto, sin hueco
   *  ni imagen rota. Los archivos originales están en `recursos/propuesta/`. */
  fotoId: string
  texto: string
  nota?: string
  /** Medidas reales del archivo. Van en el `<img>` para reservar el hueco: sin
   *  ellas la columna pega un tirón cada vez que carga una foto. */
  ancho?: number
  alto?: number
}

/* Los dos primeros momentos no llevan foto, y esa ausencia es parte de lo que
   se cuenta: de esos días no hay ninguna. */
export const RECUERDOS: ReadonlyArray<Momento> = [
  {
    fotoId: '',
    texto:
      'Agosto de 2024. Yo iba a fisiatría, recuperándome de una lesión. Entré a la sala de espera y estabas tú. No sabía quién eras, así que por respeto seguí de largo sin voltear a verte. Pero salí de ahí sabiendo que había visto algo distinto.',
    nota: 'De ese día no hay foto. Todavía era solo mío.',
  },
  {
    fotoId: '',
    texto:
      'Después, la iglesia. Dios te puso otra vez enfrente, esta vez en Su casa, y esta vez sí supe tu nombre. Nos hicimos amigos. Y ahí entendí que lo del hospital no había sido casualidad.',
    nota: 'Tampoco hay foto. Ninguno de los dos sabía para qué era.',
  },
  {
    // recursos/propuesta/1.webp
    fotoId: 'pho_szsw87bzjpkfy5atili9kt',
    texto:
      'Nuestra primera salida, solos. Sin iglesia, sin grupo, sin excusa. Los dos y nadie más.',
    ancho: 624,
    alto: 831,
  },
  {
    // recursos/propuesta/2.webp
    fotoId: 'pho_qzdcnf75ilov3gl7pgb9su',
    texto:
      'La primera con ellos. Ese día dejamos de ser dos personas conociéndose y empezamos a parecernos a una familia.',
    ancho: 1106,
    alto: 831,
  },
  {
    // recursos/propuesta/3.webp
    fotoId: 'pho_w1y6zbpxv2xu22kwpwg81y',
    texto: 'Nos fuimos de viaje. Y descubrí que el lugar da igual: lo bueno es llegar contigo.',
    ancho: 857,
    alto: 831,
  },
  {
    // recursos/propuesta/4.jpg — el único JPEG del set, y el único apaisado
    // que reemplazó a otro archivo: si vuelve a cambiar, cambia las medidas.
    fotoId: 'pho_zho7uuhy94kxpzbrtm14d9',
    texto:
      'Y otro viaje. Y otro. Ya no me acuerdo de todos los lugares. Me acuerdo de que en todos ibas tú al lado.',
    ancho: 1109,
    alto: 831,
  },
  {
    // recursos/propuesta/7.jpg — el río, con una de las niñas. Su verso es
    // Cantares 8:7: la foto tiene agua, y el agua no apaga este amor.
    fotoId: 'pho_rsyyo9yy9ps7auf8jncrrr',
    texto:
      'Frente a este río me vino un versículo: «las muchas aguas no podrán apagar el amor, ni lo ahogarán los ríos». Que venga lo que venga, no hay corriente que apague lo que siento por ti. Eres mi Cantares 8:7.',
    ancho: 1109,
    alto: 831,
  },
  {
    // recursos/propuesta/5.webp — el pico espiritual. Su verso es Proverbios
    // 19:14: delante de Dios, ella no se heredó ni se ganó; Él la dio.
    fotoId: 'pho_lyt9wpb72h0dtiebo1ble5',
    texto:
      'Buscando a Dios, juntos. Esto no es una parte de lo nuestro: es el centro. La casa y lo que uno junta con los años se heredan; pero a una mujer prudente solo la da Él. Y a mí me dio a ti. Eres mi Proverbios 19:14.',
    ancho: 922,
    alto: 811,
  },
  {
    // recursos/propuesta/6.webp
    fotoId: 'pho_kto3ju8m7dl0azqo31a1vg',
    texto: 'Y todavía nos falta mucho. Por más aventuras, contigo y con Él.',
    ancho: 1109,
    alto: 831,
  },
]

/* Una frase que alguien de la familia escriba en secreto bendiciendo el
   matrimonio, para que ella descubra que no fue solo idea de Lenin. En null
   no se pinta nada: si la nota no llega a tiempo, el pacto queda como está. */
const BENDICION: { texto: string; quien: string } | null = null

/**
 * Las fotos se sirven ENTERAS, sin `?tam=mini`, a propósito. Los seis archivos
 * (cinco WebP y un JPEG) miden 624–1109 px de ancho y pesan 70–270 KB, ya
 * comprimidos en una exportación previa; `?tam=mini` los reduciría a 600 px y
 * los volvería a codificar en WebP q72 —recomprimir lo ya comprimido: piel y
 * cielos pierden nitidez— sin ahorrar apenas bytes. A la medida en que se ven
 * de verdad (una columna de ~342 px en un celular) quedan entre 1.8× y 3.2×:
 * nítidas.
 *
 * Las medidas de cada momento salen de los archivos de `recursos/propuesta/` y
 * un test las compara con ellos: cambiar una foto sin cambiar su medida no se
 * nota hasta que la columna pega el tirón, esa noche y en su mano.
 */
function urlDeFoto(fotoId: string): string {
  return `/f/${WEDDING_ID}/${fotoId}`
}

// ---------------------------------------------------------------------------
// El sello: la hora exacta de cada sí
// ---------------------------------------------------------------------------

function leerSello(clave: string): string | null {
  try {
    return localStorage.getItem(clave)
  } catch {
    return null
  }
}

function guardarSello(clave: string, iso: string): void {
  try {
    localStorage.setItem(clave, iso)
  } catch {
    // Sin almacenamiento la página funciona igual: solo no recuerda.
  }
}

function borrarSello(clave: string): void {
  try {
    localStorage.removeItem(clave)
  } catch {
    // Si no se puede borrar, ?reiniciar no sirve; nada más se rompe.
  }
}

/** «8:47 p. m.» — la hora exacta del sí, que es lo que se guarda de recuerdo. */
function horaDe(iso: string): string {
  return new Intl.DateTimeFormat('es-EC', { hour: 'numeric', minute: '2-digit' }).format(
    new Date(iso),
  )
}

/** «3 de agosto de 2026» */
function fechaDe(iso: string): string {
  return new Intl.DateTimeFormat('es-EC', { dateStyle: 'long' }).format(new Date(iso))
}

/** «2026-08-03» para el nombre del archivo. No se recorta del ISO a propósito:
 *  en Ecuador (UTC−5) un sí de las 20:47 cae en el día siguiente en UTC, y el
 *  archivo llevaría una fecha que no es la que dice la propia imagen. */
function fechaDeArchivo(iso: string): string {
  const fecha = new Date(iso)
  const dosCifras = (numero: number) => String(numero).padStart(2, '0')
  return `${fecha.getFullYear()}-${dosCifras(fecha.getMonth() + 1)}-${dosCifras(fecha.getDate())}`
}


/** Días completos entre dos fechas. El noviazgo es «2026-08-19» (sin hora) y
 *  el sí del matrimonio un instante ISO: `aFecha` de format.ts normaliza los
 *  dos a UTC, así que no hay error de un día por la zona horaria. */
function diasEntre(desde: string, hasta: string): number {
  const a = new Date(desde.length === 10 ? `${desde}T00:00:00Z` : desde).getTime()
  const b = new Date(hasta).getTime()
  if (!Number.isFinite(a) || !Number.isFinite(b)) return 0
  return Math.max(0, Math.floor((b - a) / 86_400_000))
}

/** Un toque corto en la mano al decir que sí. Safari no la tiene y algún
 *  navegador lanza: por eso el try. */
function vibrar(): void {
  try {
    navigator.vibrate?.([16, 60, 16])
  } catch {
    // Un celular sin vibración no cambia nada de lo que pasa en pantalla.
  }
}

/**
 * Avisa al servidor de que dijo que sí. **Se dispara y se olvida**: sin await,
 * sin spinner y sin mensaje de error. El localStorage es quien manda en vivo;
 * esto es solo el respaldo que sobrevive al teléfono. Si la red falla, la
 * pantalla avanza exactamente igual.
 *
 * Va con `fetch` desnudo y no con `api.post` a propósito: `keepalive` mantiene
 * la petición aunque ella bloquee la pantalla justo después, y así un 401 no
 * puede disparar el cierre de sesión global del cliente HTTP.
 */
function avisarAlServidor(pregunta: 'novia' | 'boda'): void {
  try {
    void fetch(`/api/public/${SLUG_BODA}/propuesta/sello`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pregunta }),
      keepalive: true,
    }).catch(() => undefined)
  } catch {
    // Ni siquiera un fetch que estalla al construirse puede tocar la pantalla.
  }
}

// ---------------------------------------------------------------------------
// Los actos
// ---------------------------------------------------------------------------

type Acto =
  | 'acceso'
  | 'apertura'
  | 'recuerdos'
  | 'recuerdo-novia'
  | 'cordon'
  | 'pregunta-boda'
  | 'mirada'
  | 'pacto'

interface Arranque {
  boda: string | null
  acto: Acto
}

/**
 * Dónde abre la página. Retomar donde se quedó: si ya dijo que sí a las dos,
 * el pacto; si solo a la primera, desde su celebración. Nunca se vuelve a
 * preguntar lo dicho.
 *
 * En ensayo no se lee ni se escribe nada: Lenin practica cuantas veces quiera
 * sin dejar sellada una respuesta que ella todavía no ha dado.
 */
function prepararArranque(ensayo: boolean, reiniciar: boolean): Arranque {
  if (ensayo) return { boda: null, acto: 'acceso' }

  if (reiniciar) {
    borrarSello(CLAVE_NOVIA)
    borrarSello(CLAVE_BODA)
    return { boda: null, acto: 'acceso' }
  }

  const boda = leerSello(CLAVE_BODA)
  // La mirada no se retoma nunca: es de un solo segundo y ya pasó.
  return { boda, acto: boda ? 'pacto' : 'acceso' }
}

export default function Propuesta() {
  const [parametros] = useSearchParams()
  const ensayo = parametros.has('ensayo')

  const [arranque] = useState<Arranque>(() =>
    prepararArranque(ensayo, parametros.has('reiniciar')),
  )
  /* El noviazgo NO sale del arranque: pasó el 19 de agosto y es un hecho, no
     un sello que esta página escriba. Por eso el ensayo tampoco lo esconde —
     ensayar evita ESCRIBIR, no borra lo que ya ocurrió—, y así Lenin ve el
     acta final tal como ella la va a ver. */
  const selloNovia = NOVIAZGO
  const [selloBoda, setSelloBoda] = useState<string | null>(arranque.boda)
  const [acto, setActo] = useState<Acto>(arranque.acto)

  useEffect(() => {
    const anterior = document.title
    document.title = 'Nuestra Boda'
    return () => {
      document.title = anterior
    }
  }, [])

  /* El <main> es tinta, pero el <body> seguía en bruma: en cada carga en frío
     lo primero que se veía era una pantalla blanca —de noche, con el brillo
     alto, en la cara— y el rebote elástico de iOS destellaba una banda blanca
     detrás. `overscroll-behavior: none` mata además el pull-to-refresh, que en
     actos cortos y centrados se dispara con nada y recarga a media pregunta.
     Se devuelve al desmontar: el resto del sitio es claro. */
  useLayoutEffect(() => {
    const raiz = document.documentElement
    const cuerpo = document.body
    const previo = {
      fondoRaiz: raiz.style.backgroundColor,
      fondoCuerpo: cuerpo.style.backgroundColor,
      reboteRaiz: raiz.style.overscrollBehavior,
      reboteCuerpo: cuerpo.style.overscrollBehavior,
    }

    raiz.style.backgroundColor = TINTA
    cuerpo.style.backgroundColor = TINTA
    raiz.style.overscrollBehavior = 'none'
    cuerpo.style.overscrollBehavior = 'none'

    return () => {
      raiz.style.backgroundColor = previo.fondoRaiz
      cuerpo.style.backgroundColor = previo.fondoCuerpo
      raiz.style.overscrollBehavior = previo.reboteRaiz
      cuerpo.style.overscrollBehavior = previo.reboteCuerpo
    }
  }, [])

  /* Cada acto empieza desde arriba. Va en useLayoutEffect y de un salto —no
     `behavior: 'smooth'`, que es lo que hereda de `html { scroll-behavior }`—
     porque iOS Safari cancela un desplazamiento suave en cuanto un dedo toca
     el vidrio: con el pulgar todavía apoyado tras el toque, la página no subía
     y el acto se leía por la mitad. */
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [acto])

  /* Las fotos del acto de recuerdos se piden al abrir la página. Para cuando
     ella llegue —un minuto después— ya están en caché y no hay ni un hueco
     gris. Si alguna falla, no pasa nada: el <img> la volverá a pedir. */
  useEffect(() => {
    try {
      for (const momento of RECUERDOS) {
        if (momento.fotoId === '') continue
        const imagen = new Image()
        imagen.decoding = 'async'
        imagen.src = urlDeFoto(momento.fotoId)
      }
    } catch {
      // Sin precarga la página funciona igual: las fotos llegan más tarde.
    }
  }, [])

  /** Sella la respuesta y devuelve su hora. En ensayo no toca nada de fuera. */
  function responder(pregunta: 'novia' | 'boda'): string {
    const ahora = new Date().toISOString()
    vibrar()
    if (!ensayo) {
      guardarSello(pregunta === 'novia' ? CLAVE_NOVIA : CLAVE_BODA, ahora)
      avisarAlServidor(pregunta)
    }
    return ahora
  }

  function decirQueSiAlMatrimonio() {
    setSelloBoda(responder('boda'))
    setActo('mirada')
  }

  return (
    <main
      className={cx(
        'flex min-h-dvh flex-col items-center justify-center bg-tinta px-6 py-12 sm:px-10 sm:py-16',
        // La banda del ensayo es `fixed`: no ocupa sitio y tapaba la primera
        // línea de los actos largos. Solo al ensayar, el contenido le cede su
        // alto. La noche real no lleva banda y no paga nada.
        ensayo && 'pt-20',
      )}
    >
      {ensayo && <AvisoDeEnsayo />}

      <div className="w-full max-w-xl">
        {acto === 'acceso' && <ActoAcceso alSeguir={() => setActo('apertura')} />}
        {acto === 'apertura' && <ActoApertura alSeguir={() => setActo('recuerdos')} />}
        {acto === 'recuerdos' && <ActoRecuerdos alSeguir={() => setActo('recuerdo-novia')} />}
        {acto === 'recuerdo-novia' && (
          <ActoRecuerdoNoviazgo alSeguir={() => setActo('cordon')} />
        )}
        {acto === 'cordon' && <ActoCordon alSeguir={() => setActo('pregunta-boda')} />}
        {acto === 'pregunta-boda' && <ActoPreguntaBoda alDecirSi={decirQueSiAlMatrimonio} />}
        {acto === 'mirada' && <ActoMirada alSeguir={() => setActo('pacto')} />}
        {acto === 'pacto' && <ActoPacto novia={selloNovia} boda={selloBoda} />}
      </div>
    </main>
  )
}

/** Que sea IMPOSIBLE confundir un ensayo con la cosa real. */
function AvisoDeEnsayo() {
  return (
    <p className="fixed inset-x-0 top-0 z-50 bg-ladrillo px-4 py-1 text-center text-xs font-medium text-bruma">
      Modo ensayo — nada se guarda
    </p>
  )
}

// ---------------------------------------------------------------------------
// Entrada escalonada dentro de cada acto
// ---------------------------------------------------------------------------

/**
 * Un bloque que ya ocupa su sitio pero todavía no se ve.
 *
 * Verse y poder tocarse salen del MISMO estado, y eso es lo importante:
 *
 *   · Si escondiera solo con opacidad, el botón seguiría en su rectángulo
 *     final, invisible y vivo al tacto. Un segundo toque en el sitio del botón
 *     anterior —lo que hace cualquiera cuando una pantalla no acusa el
 *     primero— sellaría una respuesta que ella todavía no ha leído.
 *   · Y la clase `.revelar` no sirve para esconder: bajo «Reducir movimiento»
 *     el CSS la fuerza a `opacity: 1` desde fuera de toda `@layer`, y lo no
 *     encapado gana en la cascada. Quedaría un bloque visible y muerto.
 *
 * Por eso el escondite va en `visibility`, en el style del propio elemento,
 * donde ninguna media query lo alcanza — y deja el hueco hecho: montar tarde
 * recentraría la columna entera justo mientras ella lee.
 */
export function Reservado({
  visible,
  className,
  children,
}: {
  visible: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cx('revelar', className)}
      data-visible={visible ? 'true' : undefined}
      style={visible ? undefined : { visibility: 'hidden' }}
      inert={!visible}
      aria-hidden={!visible || undefined}
    >
      {children}
    </div>
  )
}

/**
 * El contenido llega por oleadas: primero el texto, después el botón. Cada
 * oleada espera su turno escondida —y por tanto fuera de alcance, ver
 * `Reservado`—; los 60 ms de más son para que el primer fotograma pinte el
 * bloque y la transición tenga de dónde salir.
 *
 * El retardo va en el reloj y no en `transition-delay` a propósito: con la
 * espera en el CSS, el bloque quedaba destapado desde el primer fotograma
 * —solo que transparente— y el botón se podía tocar segundos antes de verse.
 *
 * Con «Reducir movimiento» el escalonado se mantiene, pero cada oleada entra
 * de golpe en vez de desvanecerse. Se conserva porque el ritmo es el guion, no
 * un adorno: es lo que hace que la segunda pregunta llegue cuando debe.
 */
export function Aparece({
  retardo,
  className,
  children,
}: {
  retardo: number
  className?: string
  children: ReactNode
}) {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const reloj = window.setTimeout(() => setVisible(true), 60 + retardo)
    return () => window.clearTimeout(reloj)
  }, [retardo])

  return (
    <Reservado visible={visible} className={className}>
      {children}
    </Reservado>
  )
}

const BOTON_SEGUIR =
  'inline-flex min-h-[52px] items-center justify-center rounded-full border border-bruma/30 ' +
  'px-8 text-base font-medium text-bruma transition-colors hover:bg-bruma/10'

const BOTON_SI =
  'inline-flex min-h-[68px] items-center justify-center rounded-full bg-laton-claro px-16 ' +
  'font-display text-3xl text-tinta transition-transform hover:scale-105 active:scale-100'

const PARRAFOS = 'mx-auto max-w-prose space-y-4 text-base leading-relaxed text-palo sm:text-lg'

/** La flecha que late sobre el botón: «tu respuesta va aquí». */
function SenalDeRespuesta() {
  return (
    <>
      <p className="text-sm text-laton-claro">Tu respuesta</p>
      <ChevronDown
        size={22}
        aria-hidden="true"
        className="mx-auto mt-2 animate-bounce text-laton-claro"
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Acto 0 — el disfraz: la verificación del acceso
// ---------------------------------------------------------------------------

function ActoAcceso({ alSeguir }: { alSeguir: () => void }) {
  const [listo, setListo] = useState(false)

  // La «verificación» tarda lo justo para creerse: aquí no ocurre nada real.
  useEffect(() => {
    const reloj = window.setTimeout(() => setListo(true), 2200)
    return () => window.clearTimeout(reloj)
  }, [])

  return (
    <div className="text-center">
      <CordMark className="mx-auto h-4 w-10 text-laton-claro" />
      <p className="eyebrow mt-6 text-laton-claro">Acceso personal</p>
      <h1 className="mt-4 font-display text-3xl text-bruma">Vanessa Vega</h1>

      {!listo ? (
        <p className="mt-10 flex items-center justify-center gap-3 text-sm text-palo">
          <Spinner small />
          Verificando tu acceso…
        </p>
      ) : (
        <Aparece retardo={0} className="mt-10">
          {/* Latón claro y no verdemar: sobre tinta el verdemar da 2.81:1 y de
              noche se lee como una mancha. Esta es la línea que la empuja a
              tocar «Abrir», así que tiene que leerse. */}
          <p className="flex items-center justify-center gap-2 text-sm text-laton-claro">
            <Check size={16} aria-hidden="true" />
            Tu acceso está listo. Pero antes de entrar, hay algo solo para ti.
          </p>
          <button type="button" onClick={alSeguir} className={cx(BOTON_SEGUIR, 'mt-8')}>
            Abrir
          </button>
        </Aparece>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Acto 1 — la apertura
// ---------------------------------------------------------------------------

function ActoApertura({ alSeguir }: { alSeguir: () => void }) {
  return (
    <div className="text-center">
      <Aparece retardo={0}>
        <p className="eyebrow text-laton-claro">Para Vanessa Vega</p>
      </Aparece>

      <Aparece retardo={350} className="mt-8">
        <div className={cx(PARRAFOS, 'text-left')}>
          <p>
            Antes de que entres a ver nada, quiero decirte algo. Y prefiero decírtelo aquí,
            escrito, para que quede y lo puedas volver a leer dentro de muchos años.
          </p>
          <p>
            Dios no se ha equivocado ni una sola vez contigo. Me dio bastante más de lo que supe
            pedirle.
          </p>
          <p>
            Por eso ya no quiero seguir caminando a tu lado sin ponerle nombre a lo nuestro:
            delante de Él, delante de tu familia y de la mía, y delante de todos.
          </p>
        </div>
      </Aparece>

      <Aparece retardo={1000} className="mt-10">
        <button type="button" onClick={alSeguir} className={BOTON_SEGUIR}>
          Sigue
        </button>
      </Aparece>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Acto 2 — los recuerdos: cómo llegamos hasta aquí
// ---------------------------------------------------------------------------

/* Aquí no va ningún cordón. La trenza tiene que estrenarse en el acto del sí,
   con toda su fuerza, y no gastarse antes en un encabezado. */

function ActoRecuerdos({ alSeguir }: { alSeguir: () => void }) {
  return (
    <div>
      <Aparece retardo={0} className="text-center">
        <p className="eyebrow text-laton-claro">Cómo llegamos hasta aquí</p>
      </Aparece>

      <ol className="mt-12 space-y-14">
        {RECUERDOS.map((momento, i) => (
          <Recuerdo key={i} momento={momento} />
        ))}
      </ol>

      <div className="mt-16 text-center">
        <button type="button" onClick={alSeguir} className={BOTON_SEGUIR}>
          Sigue
        </button>
      </div>
    </div>
  )
}

/** Un momento: su foto —si ya existe— y lo que pasó. Se revela al llegar a él. */
export function Recuerdo({ momento }: { momento: Momento }) {
  const ref = useReveal<HTMLLIElement>()
  const tieneFoto = momento.fotoId !== ''

  return (
    <li ref={ref} className="revelar">
      {tieneFoto && (
        <img
          src={urlDeFoto(momento.fotoId)}
          width={momento.ancho}
          height={momento.alto}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-auto w-full rounded-xl"
        />
      )}
      <p
        className={cx(
          'max-w-prose text-base leading-relaxed text-palo sm:text-lg',
          tieneFoto && 'mt-5',
        )}
      >
        {momento.texto}
      </p>
      {momento.nota !== undefined && (
        <p className="mt-3 text-sm italic text-laton-claro">{momento.nota}</p>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Acto 3 — lo que ya dijo que sí
// ---------------------------------------------------------------------------

/**
 * Antes preguntaba «¿quieres ser mi novia?» y esperaba un SÍ. Ya no: eso pasó
 * el 19 de agosto, en persona y no por una pantalla. Volver a preguntarlo la
 * pondría a contestar algo que ya contestó, y borraría ese día.
 *
 * Así que este acto RECUERDA. Conserva las dos hebras trenzándose —ella y yo,
 * sin la tercera todavía— porque son el pie del cordón que viene después: dos
 * hebras se rompen; la tercera es Dios.
 */
function ActoRecuerdoNoviazgo({ alSeguir }: { alSeguir: () => void }) {
  const [hebras, setHebras] = useState<number[]>([0, 0, 0])
  const [hayMas, setHayMas] = useState(false)

  useEffect(() => {
    const relojes = [
      window.setTimeout(() => setHebras([1, 0, 0]), 250),
      window.setTimeout(() => setHebras([1, 1, 0]), 700),
      window.setTimeout(() => setHayMas(true), 3200),
    ]
    return () => relojes.forEach((reloj) => window.clearTimeout(reloj))
  }, [])

  return (
    <div className="text-center">
      <div aria-hidden="true">
        <CordBraid strands={hebras} className="mx-auto h-9 w-full max-w-xs text-laton-claro" />
      </div>

      <Aparece retardo={0} className="mt-8">
        <p className="eyebrow text-palo">{fechaLarga(NOVIAZGO)}</p>
      </Aparece>

      <Aparece retardo={700} className="mt-4">
        <h1 className="font-display text-[clamp(2.25rem,10vw,4.25rem)] leading-[0.95] text-bruma text-balance">
          Dijiste que sí.
        </h1>
      </Aparece>

      <Aparece retardo={1400} className="mt-8">
        <div className={PARRAFOS}>
          <p>
            Ese día te volviste mi novia. Con respeto, con pureza y con propósito. Como Dios
            manda, y hasta donde Él quiera llevarnos.
          </p>
        </div>
      </Aparece>

      <Reservado visible={hayMas} className="mt-14">
        <p className="font-display text-2xl text-laton-claro">Pero espera.</p>
        <p className="mt-2 text-base text-palo">Todavía no termina.</p>
        <button type="button" onClick={alSeguir} className={cx(BOTON_SEGUIR, "mt-8")}>
          Sigue
        </button>
      </Reservado>
    </div>
  )
}
// ---------------------------------------------------------------------------
// Acto 5 — el cordón: por qué el noviazgo no era el final
// ---------------------------------------------------------------------------

function ActoCordon({ alSeguir }: { alSeguir: () => void }) {
  // La tercera hebra llega justo con el versículo: se ve la respuesta.
  const [hebras, setHebras] = useState<number[]>([1, 1, 0])
  useEffect(() => {
    const reloj = window.setTimeout(() => setHebras([1, 1, 1]), 2400)
    return () => window.clearTimeout(reloj)
  }, [])

  return (
    <div className="text-center">
      <div aria-hidden="true">
        <CordBraid strands={hebras} className="mx-auto h-9 w-full max-w-xs text-laton-claro" />
      </div>

      <Aparece retardo={200} className="mt-8">
        <h1 className="font-display text-3xl text-bruma sm:text-4xl">Dos hebras.</h1>
        <p className="mx-auto mt-4 max-w-prose text-base leading-relaxed text-palo sm:text-lg">
          Tú y yo, eligiéndonos. Y eso, mi amor, ya es hermoso.
        </p>
      </Aparece>

      <Aparece retardo={1300} className="mt-8">
        <p className="mx-auto max-w-prose text-base leading-relaxed text-palo sm:text-lg">
          Pero dos hebras solas se pueden romper.
        </p>
      </Aparece>

      <Aparece retardo={2400} className="mt-10">
        <blockquote className="mx-auto max-w-md">
          <p className="font-display text-xl leading-snug text-bruma sm:text-2xl">
            «Cordón de tres dobleces no se rompe pronto».
          </p>
          <footer className="eyebrow mt-3 text-palo">Eclesiastés 4:12</footer>
        </blockquote>
        <p className="mx-auto mt-6 max-w-prose text-base leading-relaxed text-palo sm:text-lg">
          La tercera hebra es Él. Y un cordón de tres no se trenza para una temporada: se trenza
          para toda la vida.
        </p>
      </Aparece>

      <Aparece retardo={3400} className="mt-12">
        <button type="button" onClick={alSeguir} className={BOTON_SEGUIR}>
          ¿Qué me quieres decir?
        </button>
      </Aparece>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Acto 6 — la pregunta que lo cambia todo
// ---------------------------------------------------------------------------

function ActoPreguntaBoda({ alDecirSi }: { alDecirSi: () => void }) {
  return (
    <div className="text-center">
      <Aparece retardo={0}>
        <p className="eyebrow text-laton-claro">
          Con la bendición de Dios y con propósito eterno
        </p>
      </Aparece>

      <Aparece retardo={600} className="mt-8">
        <p className="font-display text-2xl text-palo">Vanessa Vega…</p>
      </Aparece>

      <Aparece retardo={1400} className="mt-6">
        <h1 className="font-display text-[clamp(2.5rem,11vw,4.75rem)] leading-[0.95] text-bruma text-balance">
          ¿Te quieres casar conmigo?
        </h1>
      </Aparece>

      <Aparece retardo={2200} className="mt-8">
        <p className="mx-auto max-w-prose text-base leading-relaxed text-palo sm:text-lg">
          Para toda la vida. Delante de Él, que es quien nos sostiene.
        </p>
      </Aparece>

      <Aparece retardo={2800} className="mt-12">
        <SenalDeRespuesta />
        <button type="button" onClick={alDecirSi} className={cx(BOTON_SI, 'mt-4')}>
          SÍ
        </button>
      </Aparece>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Acto 7 — la mirada: la página se aparta
// ---------------------------------------------------------------------------

/* El paso al pacto se abre por dos caminos, y hacen falta los dos.

   El de siempre son treinta segundos: el rato del anillo. La página no avanza
   sola —ese segundo no lo apura un reloj— y cuando ella recoja el teléfono el
   paso ya estará ahí.

   El otro es su dedo. Si no bajó el teléfono y se quedó mirando la pantalla,
   una pantalla quieta se lee como trabada, y esperar medio minuto sin saber si
   algo va a pasar es lo peor que puede hacer esta página justo aquí. Un toque
   destapa el paso. No lo adelanta ni la distrae: solo responde a una atención
   que ya estaba puesta en el teléfono.

   Los diez primeros segundos no escuchan nada, y son los que importan: ahí es
   donde ella baja el brazo y donde un roce del pulgar no puede sacar un botón
   en mitad del gesto. */
const ESPERA_MIRADA_MS = 30_000
const GRACIA_MIRADA_MS = 10_000

/* Se exporta solo para su test: llegar hasta aquí por la pantalla cuesta medio
   minuto de temporizadores, y este es el único paso al pacto. */
export function ActoMirada({ alSeguir }: { alSeguir: () => void }) {
  const [hayPaso, setHayPaso] = useState(false)
  const [escucha, setEscucha] = useState(false)

  useEffect(() => {
    const relojes = [
      window.setTimeout(() => setEscucha(true), GRACIA_MIRADA_MS),
      window.setTimeout(() => setHayPaso(true), ESPERA_MIRADA_MS),
    ]
    return () => relojes.forEach((reloj) => window.clearTimeout(reloj))
  }, [])

  useEffect(() => {
    if (!escucha || hayPaso) return
    const destapar = () => setHayPaso(true)
    window.addEventListener('pointerdown', destapar, { once: true })
    return () => window.removeEventListener('pointerdown', destapar)
  }, [escucha, hayPaso])

  return (
    <div className="text-center">
      <Aparece retardo={0}>
        <h1 className="font-display text-[clamp(2.25rem,9vw,3.75rem)] leading-[1.05] text-bruma text-balance">
          Ahora baja el teléfono.
        </h1>
      </Aparece>

      <Aparece retardo={900} className="mt-6">
        <p className="text-base text-laton-claro">Estoy aquí.</p>
      </Aparece>

      <Reservado visible={hayPaso} className="mt-20">
        <button type="button" onClick={alSeguir} className={BOTON_SEGUIR}>
          Sigue
        </button>
      </Reservado>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Acto 8 — el pacto: las dos horas selladas
// ---------------------------------------------------------------------------

function ActoPacto({ novia, boda }: { novia: string | null; boda: string | null }) {
  const [hebras, setHebras] = useState<number[]>([0, 0, 0])
  useEffect(() => {
    const relojes = [0, 1, 2].map((i) =>
      window.setTimeout(
        () => setHebras((previas) => previas.map((valor, j) => (j <= i ? 1 : valor))),
        250 + i * 450,
      ),
    )
    return () => relojes.forEach((reloj) => window.clearTimeout(reloj))
  }, [])

  /* Antes eran dos horas de la misma noche y se contaban los minutos entre
     ellas. Ahora hay meses de por medio: lo que se cuenta son los días que
     fue mi novia antes de que le pidiera ser mi esposa. */
  const dias = novia && boda ? diasEntre(novia, boda) : null

  return (
    <div className="text-center">
      <div aria-hidden="true">
        <CordBraid strands={hebras} className="mx-auto h-10 w-full max-w-sm text-laton-claro" />
      </div>
      <p className="eyebrow mt-4 text-palo">Tú · Yo · Él</p>

      <Aparece retardo={900} className="mt-10">
        <h1 className="font-display text-[clamp(2.5rem,11vw,5rem)] leading-[0.95] text-bruma">
          Dijiste que sí.
        </h1>
      </Aparece>

      {/* El acta del día: las dos horas exactas, para que no se olviden. */}
      {novia && boda && (
        <Aparece retardo={1500} className="mt-10">
          <div className="mx-auto max-w-sm border-l-2 border-laton-claro py-1 pl-5 text-left">
            <p className="eyebrow text-palo">Las dos veces que dijiste que sí</p>
            <p className="mt-3 text-base text-palo">
              <span className="text-bruma">{fechaLarga(novia)}</span> · me dijiste que sí a ser
              mi novia.
            </p>
            <p className="mt-2 text-base text-palo">
              Hoy, <span className="text-bruma">{fechaDe(boda)}</span> a las{' '}
              <span className="tabular font-mono text-bruma">{horaDe(boda)}</span> · me dijiste
              que sí a ser mi esposa.
            </p>
            {dias !== null && dias > 0 && (
              <p className="mt-4 text-sm text-laton-claro">
                {dias === 1 ? 'Un día' : `${dias} días`} siendo mi novia. Los más felices de mi
                vida — hasta hoy.
              </p>
            )}
          </div>
        </Aparece>
      )}

      {/* La bendición de la familia, si alguien la escribió a tiempo. */}
      {BENDICION && (
        <Aparece retardo={1900} className="mt-10">
          <blockquote className="mx-auto max-w-sm border-l-2 border-laton py-1 pl-5 text-left">
            <p className="text-base leading-relaxed text-palo">«{BENDICION.texto}»</p>
            <footer className="eyebrow mt-3 text-palo">{BENDICION.quien}</footer>
          </blockquote>
        </Aparece>
      )}

      <Aparece retardo={2100} className="mt-10">
        <div className={cx(PARRAFOS, 'text-left')}>
          <p>
            Que Dios guarde este pacto, que lo dirija y que nos enseñe a amarnos como Él nos amó
            primero — y que en cada paso, hasta el altar y mucho después del altar, Él sea el
            glorificado.
          </p>
          <p>Gracias por decir que sí. Dos veces.</p>
          <p>
            La fecha todavía no la tenemos, y está bien: la ponemos en las manos de Dios y
            esperamos a que Él nos la muestre. Lo que sí tenemos desde hoy es el sí, y el resto
            lo preparamos juntos.
          </p>
        </div>
      </Aparece>

      <Aparece retardo={2700} className="mt-10">
        <p className="font-display text-2xl text-bruma">Bienvenida a nuestro plan de boda.</p>
        <div className="mt-6">
          <Link
            to="/entrar"
            className="inline-flex min-h-[52px] items-center justify-center rounded-full bg-bruma px-8 text-base font-medium text-tinta transition-colors hover:bg-palo"
          >
            Entrar a nuestro plan
          </Link>
          <p className="mt-4 text-xs text-palo">Tu clave la tiene Lenin. Pídesela.</p>
        </div>
      </Aparece>

      {novia && boda && (
        <Aparece retardo={3200} className="mt-12">
          <button
            type="button"
            onClick={() => descargarRecuerdo(novia, boda)}
            className={BOTON_SEGUIR}
          >
            Descargar el recuerdo
          </button>
          <p className="mt-3 text-xs text-palo">Se guarda en tu galería.</p>
        </Aparece>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// El recuerdo en PNG — dibujado aquí, sin servidor y sin dependencias
// ---------------------------------------------------------------------------

/** Vertical: así se guarda bien en la galería del teléfono. */
const LIENZO_ANCHO = 1080
const LIENZO_ALTO = 1350
const MARGEN = 90

function fuenteDe(tamano: number): string {
  return `${tamano}px ${FAMILIA_DISPLAY}`
}

/** Parte un texto en líneas que quepan. */
function partirEnLineas(ctx: CanvasRenderingContext2D, texto: string, maximo: number): string[] {
  const lineas: string[] = []
  let actual = ''

  for (const palabra of texto.split(' ')) {
    const intento = actual === '' ? palabra : `${actual} ${palabra}`
    if (actual !== '' && ctx.measureText(intento).width > maximo) {
      lineas.push(actual)
      actual = palabra
    } else {
      actual = intento
    }
  }
  if (actual !== '') lineas.push(actual)

  return lineas
}

/**
 * Escribe centrado y devuelve la Y de después. Si no cabe, primero encoge la
 * letra y solo si aun así no entra la parte en varias líneas: un recuerdo con
 * una palabra cortada por el borde no se guarda nadie.
 */
function escribir(
  ctx: CanvasRenderingContext2D,
  texto: string,
  opciones: { y: number; tamano: number; color: string },
): number {
  const maximo = LIENZO_ANCHO - MARGEN * 2
  const minimo = Math.round(opciones.tamano * 0.6)

  let tamano = opciones.tamano
  ctx.font = fuenteDe(tamano)
  while (ctx.measureText(texto).width > maximo && tamano > minimo) {
    tamano -= 2
    ctx.font = fuenteDe(tamano)
  }

  const lineas =
    ctx.measureText(texto).width > maximo ? partirEnLineas(ctx, texto, maximo) : [texto]

  ctx.fillStyle = opciones.color
  let y = opciones.y
  for (const linea of lineas) {
    ctx.fillText(linea, LIENZO_ANCHO / 2, y)
    y += Math.round(tamano * 1.3)
  }
  return y
}

/** El glifo del cordón: las mismas tres hebras senoidales del sitio, con la
 *  matemática de `puntosDeHebra` (src/client/ui/Cord.tsx), trazadas a mano. */
function dibujarCordon(ctx: CanvasRenderingContext2D, y: number): void {
  const largo = 460
  const alto = 56
  const x = (LIENZO_ANCHO - largo) / 2

  ctx.lineWidth = 3
  ctx.lineCap = 'round'
  ctx.strokeStyle = LATON_CLARO

  FASES.forEach((fase, i) => {
    ctx.globalAlpha = 0.5 + i * 0.25
    ctx.beginPath()
    const puntos = puntosDeHebra({
      largo,
      ancho: alto,
      vueltas: 1.5,
      fase,
      vertical: false,
      pasos: 160,
    })
    puntos.forEach(([dx, dy], indice) => {
      if (indice === 0) ctx.moveTo(x + dx, y + dy)
      else ctx.lineTo(x + dx, y + dy)
    })
    ctx.stroke()
  })

  ctx.globalAlpha = 1
}

function pintarRecuerdo(ctx: CanvasRenderingContext2D, novia: string, boda: string): void {
  ctx.fillStyle = TINTA
  ctx.fillRect(0, 0, LIENZO_ANCHO, LIENZO_ALTO)

  dibujarCordon(ctx, 170)

  ctx.textAlign = 'center'
  ctx.textBaseline = 'alphabetic'

  let y = escribir(ctx, 'Vanessa y Lenin', { y: 400, tamano: 92, color: BRUMA })
  y = escribir(ctx, fechaDe(boda), { y: y + 24, tamano: 38, color: PALO })

  y = escribir(ctx, `${horaDe(novia)} · me dijiste que sí a ser mi novia`, {
    y: y + 96,
    tamano: 34,
    color: BRUMA,
  })
  y = escribir(ctx, `${horaDe(boda)} · me dijiste que sí a ser mi esposa`, {
    y: y + 14,
    tamano: 34,
    color: BRUMA,
  })

  y = escribir(ctx, 'Cordón de tres dobleces no se rompe pronto', {
    y: y + 130,
    tamano: 46,
    color: BRUMA,
  })
  escribir(ctx, 'Eclesiastés 4:12', { y: y + 20, tamano: 28, color: LATON_CLARO })

  escribir(ctx, '19 de diciembre de 2026', {
    y: LIENZO_ALTO - 140,
    tamano: 36,
    color: PALO,
  })
}

/**
 * Baja el recuerdo como PNG. Todo ocurre en el navegador: esa noche no puede
 * depender de la red. Si el navegador va en modo restringido y no da lienzo,
 * no pasa nada —se queda sin recuerdo, no roto—.
 */
function descargarRecuerdo(novia: string, boda: string): void {
  try {
    const lienzo = document.createElement('canvas')
    lienzo.width = LIENZO_ANCHO
    lienzo.height = LIENZO_ALTO

    const ctx = lienzo.getContext('2d')
    if (!ctx) return
    pintarRecuerdo(ctx, novia, boda)

    lienzo.toBlob((blob) => {
      if (!blob) return
      const url = URL.createObjectURL(blob)
      const enlace = document.createElement('a')
      enlace.href = url
      enlace.download = `nuestro-si-${fechaDeArchivo(boda)}.png`
      document.body.appendChild(enlace)
      enlace.click()
      enlace.remove()
      // Revocar de inmediato corta la descarga en algunos navegadores.
      window.setTimeout(() => URL.revokeObjectURL(url), 2000)
    }, 'image/png')
  } catch {
    // Un recuerdo que no se pudo bajar no puede tumbar la página del sí.
  }
}
